import { create } from "zustand"
import { persist } from "zustand/middleware"

import {
  createOutboundTask,
  deleteOutboundTask,
  getOutboundTasks,
  patchOutboundTask
} from "~/api/agent-os"
import {
  AccountPlanPhase,
  IAccountPlanProgress,
  IAccountPlanStep,
  IAgentTask,
  IAgentWorkflow,
  IOutboundAgentProgress,
  IOutboundAgentStep,
  IOutboundStakeholder,
  IWorkflowRun,
  OutboundAgentPhase
} from "~/types/agent-os"
import { ISequenceTemplate } from "~/api/prospectos"
import { ITokenInfo } from "~/types/global"

import useGlobalStore from "./global"

const tz = Intl.DateTimeFormat().resolvedOptions().timeZone

const DEFAULT_WORKFLOWS: IAgentWorkflow[] = [
  {
    id: "digest-default",
    tenant_id: 0,
    user_id: 0,
    workflow_type: "digest",
    name: "Periodic Digest",
    description: "Get curated account intelligence delivered on your schedule",
    is_enabled: false,
    is_published: true,
    config: {
      sections: [
        "executive_summary",
        "priorities",
        "pipeline",
        "executive_transitions",
        "job_openings",
        "industry_trends",
        "news_feed",
        "accounts_tracker"
      ],
      frequency: "daily",
      time: "08:00",
      timezone: tz,
      account_scope: "all",
      account_ids: [],
      channels: ["email"]
    },
    schedule_cron: null,
    timezone: tz,
    last_run_at: null,
    next_run_at: null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  },
  // Outbound workflow is fetched from DB — not seeded here
  /* {
    id: "outbound-default",
    tenant_id: 0,
    user_id: 0,
    workflow_type: "outbound",
    name: "Outbound",
    description: "Prospect new leads and engage on autopilot",
    is_enabled: false,
    is_published: true,
    config: {
      icp: {
        industries: [],
        revenue_range: [],
        employee_range: [],
        geographies: []
      },
      persona: {
        titles: [],
        seniority_levels: []
      },
      product_description: "",
      outreach_instructions: "",
      review_before_send: true,
      daily_summary_time: "17:00",
      integrations: {
        email_provider: null,
        email_connected: false,
        linkedin_connected: false,
        phone_provider: null,
        phone_connected: false,
        knowledge_provider: null,
        knowledge_connected: false,
        calendar_provider: null,
        calendar_connected: false
      }
    },
    schedule_cron: null,
    timezone: null,
    last_run_at: null,
    next_run_at: null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  }, */
  {
    id: "opportunities-default",
    tenant_id: 0,
    user_id: 0,
    workflow_type: "opportunities",
    name: "Opportunities & Renewals",
    description: "Get notified about new opportunities and upcoming renewals",
    is_enabled: false,
    is_published: true,
    config: {
      enable_new_opportunities: true,
      enable_renewal_deals: true,
      frequency: "daily",
      account_scope: "all",
      account_ids: [],
      min_deal_value: null,
      seniority_filter: "director_plus",
      channels: ["email"]
    },
    schedule_cron: null,
    timezone: null,
    last_run_at: null,
    next_run_at: null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  },
  {
    id: "forecast-default",
    tenant_id: 0,
    user_id: 0,
    workflow_type: "forecast",
    name: "Weekly Forecast",
    description: "Auto-generate weekly sales forecast reports",
    is_enabled: false,
    is_published: true,
    config: {
      day_of_week: 1,
      time: "09:00",
      timezone: tz,
      account_scope: "all",
      account_ids: [],
      report_format: "summary",
      meeting_source_connected: false
    },
    schedule_cron: "0 9 * * 1",
    timezone: tz,
    last_run_at: null,
    next_run_at: null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  },
  {
    id: "dashboard-default",
    tenant_id: 0,
    user_id: 0,
    workflow_type: "dashboard",
    name: "Your Command Center",
    description: "Tasks, opportunities, renewals, and upcoming meetings",
    is_enabled: false,
    is_published: true,
    config: {} as any,
    schedule_cron: "0 8 * * *",
    timezone: tz,
    last_run_at: null,
    next_run_at: null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  }
]

type AgentOsStore = {
  // User context — used to scope persisted state per user
  _userId: number | null
  _tenantId: number | null
  setUserContext: (userId: number, tenantId: number) => void

  // Workflows data
  workflows: IAgentWorkflow[]
  setWorkflows: (workflows: IAgentWorkflow[]) => void
  updateWorkflow: (workflowId: string, updates: Partial<IAgentWorkflow>) => void
  toggleWorkflow: (workflowId: string, enabled: boolean) => void
  addWorkflow: (workflow: IAgentWorkflow) => void

  // Run history
  workflowRuns: IWorkflowRun[]
  triggerRun: (
    workflowId: string,
    triggerType: "manual" | "test"
  ) => IWorkflowRun

  // New workflow modal
  showNewWorkflowModal: boolean
  setShowNewWorkflowModal: (show: boolean) => void

  // Test run modal
  showTestRunModal: boolean
  testRunWorkflowId: string | null
  setShowTestRunModal: (show: boolean, workflowId?: string | null) => void

  // Sequence/campaign templates modal (Prospect OS). Shared so the header
  // "Templates" button and the setup wizard's "Choose Template" open the same one.
  showCampaignTemplatesModal: boolean
  setShowCampaignTemplatesModal: (show: boolean) => void

  // Bumped whenever a sequence template is applied; a counter (not a bool) so re-applying the same template still re-fires the table's regenerate prompt.
  templateRegenSignal: number
  signalTemplateRegen: () => void

  // Wizard template pick. A not-yet-created agent has no id to PUT against, so
  // it's staged here, folded into the saved config, and cleared on close.
  pendingSequenceTemplate: ISequenceTemplate | null
  setPendingSequenceTemplate: (template: ISequenceTemplate | null) => void

  // Active workflow for detail pages
  activeWorkflow: IAgentWorkflow | null
  setActiveWorkflow: (workflow: IAgentWorkflow | null) => void

  // Outbound agent execution state
  outboundRelevantRoles: string[]
  outboundLlmTitleExpansions: Record<string, string[]>
  setOutboundRelevanceData: (
    roles: string[],
    expansions: Record<string, string[]>
  ) => void
  outboundAllMatchedAccounts: any[]
  outboundMatchedAccounts: any[]
  setOutboundMatchedAccounts: (accounts: any[]) => void
  loadMoreAccounts: () => void
  outboundSelectedMatchedAccountIds: string[]
  setOutboundSelectedMatchedAccountIds: (ids: string[]) => void
  toggleOutboundMatchedAccountSelection: (id: string) => void
  // Snapshot of the selection taken when the user opens the People list; this
  // is the persisted set restored on refresh (live selection is not persisted).
  outboundCommittedAccountIds: string[]
  commitOutboundAccountSelection: () => void
  // Single "active" account whose rationale panel is open (separate from the
  // checkbox multi-select). Transient UI state — not persisted.
  outboundActiveAccountId: string | null
  setOutboundActiveAccountId: (id: string | null) => void
  outboundReviewAcknowledged: boolean
  setOutboundReviewAcknowledged: (v: boolean) => void
  outboundStakeholders: IOutboundStakeholder[]
  setOutboundStakeholders: (stakeholders: IOutboundStakeholder[]) => void
  addOutboundStakeholder: (stakeholder: IOutboundStakeholder) => void
  // IDs added via Add Person / bulk upload this session — lets the table
  // pin them to the top without changing outboundStakeholders' own order
  // (that order still reflects the run's top-ranked discovery results).
  outboundRecentlyAddedStakeholderIds: string[]
  removeOutboundStakeholder: (id: string) => void
  updateOutboundStakeholder: (
    id: string,
    updates: Partial<IOutboundStakeholder>
  ) => void
  outboundProgress: IOutboundAgentProgress
  setOutboundPhase: (phase: OutboundAgentPhase) => void
  addOutboundStep: (step: IOutboundAgentStep) => void
  updateOutboundStep: (
    stepId: string,
    updates: Partial<IOutboundAgentStep>
  ) => void
  setOutboundSteps: (steps: IOutboundAgentStep[]) => void
  resetOutboundAgent: () => void
  // Live run lifecycle — shared so polling can be owned by a tab-independent
  // parent (not the AgentRunner component, which unmounts on tab switch).
  outboundCurrentRunId: string | null
  setOutboundCurrentRunId: (runId: string | null) => void
  outboundIsRunning: boolean
  setOutboundIsRunning: (v: boolean) => void
  // Poller's view of the latest run; "unknown" until the mount fetch settles, which own-list auto-run must wait for or it re-runs every refresh.
  outboundLatestRunState: "unknown" | "none" | "exists"
  setOutboundLatestRunState: (s: "unknown" | "none" | "exists") => void
  // The run the user explicitly reset away from. The poller's mount-recovery
  // skips re-hydrating this run, so resetting then navigating back stays clean.
  outboundDismissedRunId: string | null
  // Run ids whose uploaded-accounts "N of M will continue" modal the user has
  // already acknowledged. PERSISTED (see partialize) — it's a one-time
  // explanation, so a page reload must not re-show it for the same run.
  //
  // A list, not a single id: a user alternating between two workflows would
  // otherwise have each dismissal overwrite the other's, re-showing the modal
  // on every switch. Capped since nothing ever clears it.
  outboundUploadReviewDismissedRunIds: string[]
  acknowledgeOutboundUploadReview: (runId: string | null) => void
  // Per-workflow: user explicitly stopped monitoring → don't auto-restart on
  // remount until they hit Start again.
  outboundMonitoringPaused: Record<string, boolean>
  setOutboundMonitoringPaused: (workflowId: string, paused: boolean) => void
  // PROTOTYPE prospectOS: per-agent session state
  outboundCustomProspectList: { fileName: string } | null
  setOutboundCustomProspectList: (payload: { fileName: string } | null) => void
  outboundSessions: Record<
    string,
    {
      stakeholders: IOutboundStakeholder[]
      progress: IOutboundAgentProgress
      matchedAccounts: any[]
      allMatchedAccounts: any[]
      customProspectList: { fileName: string } | null
      selectedStakeholderIds: string[]
      seenStakeholderIds: string[]
      currentRunId: string | null
      isRunning: boolean
    }
  >
  snapshotOutboundSession: (workflowId: string) => void
  loadOutboundSession: (workflowId: string) => void

  // Account plan agent execution state
  accountPlanProgress: IAccountPlanProgress
  setAccountPlanPhase: (phase: AccountPlanPhase) => void
  addAccountPlanStep: (step: IAccountPlanStep) => void
  updateAccountPlanStep: (
    stepId: string,
    updates: Partial<IAccountPlanStep>
  ) => void
  resetAccountPlan: () => void

  // Outbound stakeholder selection
  selectedStakeholderIds: string[]
  toggleStakeholderSelection: (id: string) => void
  selectAllStakeholders: () => void
  deselectAllStakeholders: () => void
  setSelectedStakeholderIds: (ids: string[]) => void

  // "Seen" guards for the default auto-selection. Persisted in the store (not a
  // per-mount ref) so switching tabs — which unmounts the people/accounts
  // panels — doesn't re-run the initial auto-select and clobber the user's
  // manual de/selections.
  outboundSeenStakeholderIds: string[]
  markOutboundStakeholdersSeen: (ids: string[]) => void
  outboundSeenAccountIds: string[]
  markOutboundAccountsSeen: (ids: string[]) => void

  // Per-account people_search pagination + a per-run guard for the navigation
  // auto-fetch. Persisted in the store (not per-mount refs) so tab switches
  // don't re-issue hot_accounts/people_search for already-fetched accounts.
  outboundAccountPages: Record<
    string,
    {
      page: number
      hasMore: boolean
      searchId: string | null
      // Per-seniority pagination cursors (people_search is fetched with one
      // parallel call per seniority). Keyed by seniority level. Top-level
      // hasMore is the OR across lanes.
      laneCursors?: Record<
        string,
        { page: number; hasMore: boolean; searchId: string | null }
      >
    }
  >
  setOutboundAccountPage: (
    accountId: string,
    entry: {
      page: number
      hasMore: boolean
      searchId: string | null
      laneCursors?: Record<
        string,
        { page: number; hasMore: boolean; searchId: string | null }
      >
    }
  ) => void
  outboundAutoFetchedRunId: string | null
  setOutboundAutoFetchedRunId: (runId: string | null) => void

  // Email connection — `emailConnected` is sourced live from the backend via
  // useGetEmailConnectionStatus. Provider preference is kept locally so the
  // wizard can offer the right reconnect flow.
  emailProvider: "outlook" | "gmail" | null
  setEmailProvider: (provider: "outlook" | "gmail" | null) => void

  // Outbound setup wizard
  showOutboundSetupWizard: boolean
  setShowOutboundSetupWizard: (show: boolean) => void

  // Processed message IDs (to prevent duplicate replies)
  // (monitoring reply log + processed-message dedup now live on the backend
  //  in agent_stakeholder_replies; monitor-tick + GET /replies are the API.)

  // Dashboard tasks
  tasks: IAgentTask[]
  addTask: (task: IAgentTask) => void
  completeTask: (taskId: string) => void
  snoozeTask: (taskId: string, until: string) => void
  deleteTask: (taskId: string) => void
  loadTasks: () => Promise<void>

  // (autoRespond now lives on the workflow config so the backend monitor
  //  service can read it; the UI toggle updates it via the workflow PUT.)

  // Preview digest modal
  showDigestPreview: boolean
  setShowDigestPreview: (show: boolean) => void

  // Notification
  lastNotification: { type: "success" | "error"; message: string } | null
  setLastNotification: (
    n: { type: "success" | "error"; message: string } | null
  ) => void
}

const INITIAL_OUTBOUND_PROGRESS: IOutboundAgentProgress = {
  phase: "idle",
  steps: []
}

const INITIAL_ACCOUNT_PLAN_PROGRESS: IAccountPlanProgress = {
  phase: "idle",
  steps: []
}

// How many acknowledged upload-review run ids to keep. Nothing ever clears
// this list, so it needs a ceiling — but it only has to cover the runs a user
// might realistically navigate back to, and re-showing the modal for a run
// this old is harmless.
const UPLOAD_REVIEW_ACK_HISTORY = 50

function stableStringify(value: unknown) {
  return value == null ? "" : JSON.stringify(value)
}

function areOutboundStepsEqual(
  current: IOutboundAgentStep[],
  next: IOutboundAgentStep[]
) {
  if (current === next) return true
  if (current.length !== next.length) return false
  for (let i = 0; i < current.length; i++) {
    const a = current[i]
    const b = next[i]
    if (
      a.id !== b.id ||
      a.title !== b.title ||
      a.detail !== b.detail ||
      a.status !== b.status
    ) {
      return false
    }
  }
  return true
}

function areOutboundStakeholdersEqual(
  current: IOutboundStakeholder[],
  next: IOutboundStakeholder[]
) {
  if (current === next) return true
  if (current.length !== next.length) return false
  for (let i = 0; i < current.length; i++) {
    const a = current[i]
    const b = next[i]
    if (
      a.id !== b.id ||
      a.name !== b.name ||
      a.title !== b.title ||
      a.company !== b.company ||
      a.linkedin_url !== b.linkedin_url ||
      a.seniority !== b.seniority ||
      a.email !== b.email ||
      a.phone !== b.phone ||
      a.is_exclusion !== b.is_exclusion ||
      a.exclusion_reason !== b.exclusion_reason ||
      a.enrichment_status !== b.enrichment_status ||
      a.email_subject !== b.email_subject ||
      a.engagement_status !== b.engagement_status ||
      a.country !== b.country ||
      a.initial_send_at !== b.initial_send_at ||
      a.last_activity !== b.last_activity ||
      stableStringify(a.drafts) !== stableStringify(b.drafts) ||
      stableStringify(a.calendar_events) !== stableStringify(b.calendar_events)
    ) {
      return false
    }
  }
  return true
}

const useAgentOsStore = create<AgentOsStore>()(
  persist(
    (set, get) => ({
      // User context
      _userId: null,
      _tenantId: null,
      setUserContext: (userId, tenantId) => {
        const state = get()
        const isSameUser =
          state._userId === userId && state._tenantId === tenantId
        if (!isSameUser) {
          set({
            _userId: userId,
            _tenantId: tenantId,
            workflows: [...DEFAULT_WORKFLOWS],
            workflowRuns: [],
            tasks: [],
            outboundAllMatchedAccounts: [],
            outboundMatchedAccounts: [],
            outboundStakeholders: [],
            outboundRecentlyAddedStakeholderIds: [],
            outboundRelevantRoles: [],
            outboundLlmTitleExpansions: {},
            outboundProgress: { ...INITIAL_OUTBOUND_PROGRESS },
            outboundCurrentRunId: null,
            outboundIsRunning: false,
            // Persisted, so it would otherwise outlive the user it belongs to
            // — these are another tenant's run ids. They could never match the
            // incoming user's runs, but there's no reason to keep them around.
            outboundUploadReviewDismissedRunIds: [],
            selectedStakeholderIds: [],
            emailProvider: null
          })
        }
      },

      // Mutable workflows
      workflows: [...DEFAULT_WORKFLOWS],
      setWorkflows: (workflows) => set({ workflows }),
      updateWorkflow: (workflowId, updates) =>
        set((state) => ({
          workflows: state.workflows.map((w) =>
            w.id === workflowId
              ? { ...w, ...updates, updated_at: new Date().toISOString() }
              : w
          ),
          lastNotification: {
            type: "success",
            message: "Configuration saved successfully"
          }
        })),
      toggleWorkflow: (workflowId, enabled) =>
        set((state) => ({
          workflows: state.workflows.map((w) =>
            w.id === workflowId
              ? {
                  ...w,
                  is_enabled: enabled,
                  updated_at: new Date().toISOString()
                }
              : w
          ),
          lastNotification: {
            type: "success",
            message: enabled ? "Workflow enabled" : "Workflow disabled"
          }
        })),
      addWorkflow: (workflow) =>
        set((state) => ({
          workflows: [...state.workflows, workflow]
        })),

      // Run history
      workflowRuns: [],
      triggerRun: (workflowId, triggerType) => {
        const now = new Date().toISOString()
        const run: IWorkflowRun = {
          id: crypto.randomUUID(),
          workflow_id: workflowId,
          tenant_id: 0,
          user_id: 0,
          status: "success",
          started_at: now,
          completed_at: now,
          output: {
            message:
              triggerType === "test"
                ? "Test run completed successfully (preview only, no notifications sent)"
                : "Workflow executed successfully"
          },
          error: null,
          trigger_type: triggerType
        }
        set((state) => ({
          workflowRuns: [run, ...state.workflowRuns],
          workflows: state.workflows.map((w) =>
            w.id === workflowId ? { ...w, last_run_at: now } : w
          ),
          lastNotification: {
            type: "success",
            message:
              triggerType === "test"
                ? "Test run completed"
                : "Workflow executed successfully"
          }
        }))
        return run
      },

      showNewWorkflowModal: false,
      setShowNewWorkflowModal: (show) => set({ showNewWorkflowModal: show }),

      showTestRunModal: false,
      testRunWorkflowId: null,
      setShowTestRunModal: (show, workflowId = null) =>
        set({ showTestRunModal: show, testRunWorkflowId: workflowId }),

      showCampaignTemplatesModal: false,
      setShowCampaignTemplatesModal: (show) =>
        set({ showCampaignTemplatesModal: show }),

      templateRegenSignal: 0,
      signalTemplateRegen: () =>
        set((state) => ({
          templateRegenSignal: state.templateRegenSignal + 1
        })),

      pendingSequenceTemplate: null,
      setPendingSequenceTemplate: (template) =>
        set({ pendingSequenceTemplate: template }),

      activeWorkflow: null,
      setActiveWorkflow: (workflow) => set({ activeWorkflow: workflow }),

      // Outbound agent execution
      outboundRelevantRoles: [],
      outboundLlmTitleExpansions: {},
      setOutboundRelevanceData: (roles, expansions) =>
        set({
          outboundRelevantRoles: roles,
          outboundLlmTitleExpansions: expansions
        }),
      outboundAllMatchedAccounts: [],
      outboundMatchedAccounts: [],
      setOutboundMatchedAccounts: (accounts) =>
        set({ outboundMatchedAccounts: accounts }),
      loadMoreAccounts: () =>
        set((state) => {
          const current = state.outboundMatchedAccounts.length
          const next = state.outboundAllMatchedAccounts.slice(0, current + 20)
          return { outboundMatchedAccounts: next }
        }),
      outboundSelectedMatchedAccountIds: [],
      setOutboundSelectedMatchedAccountIds: (ids) =>
        set({ outboundSelectedMatchedAccountIds: ids }),
      outboundCommittedAccountIds: [],
      commitOutboundAccountSelection: () =>
        set((state) => {
          const prev = state.outboundCommittedAccountIds
          const next = state.outboundSelectedMatchedAccountIds
          const accountsChanged =
            prev.length !== next.length ||
            !prev.every((id) => next.includes(id))
          return {
            outboundCommittedAccountIds: next,
            // A changed account selection invalidates whatever stakeholders
            // were previously picked — the People list is about to refetch
            // for a different account set, so a stale "N selected" toolbar
            // (with live Start-sending/Remove actions) would be misleading.
            selectedStakeholderIds: accountsChanged
              ? []
              : state.selectedStakeholderIds
          }
        }),
      outboundActiveAccountId: null,
      setOutboundActiveAccountId: (id) => set({ outboundActiveAccountId: id }),
      toggleOutboundMatchedAccountSelection: (id) =>
        set((state) => {
          const has = state.outboundSelectedMatchedAccountIds.includes(id)
          return {
            outboundSelectedMatchedAccountIds: has
              ? state.outboundSelectedMatchedAccountIds.filter((x) => x !== id)
              : [...state.outboundSelectedMatchedAccountIds, id]
          }
        }),
      outboundReviewAcknowledged: false,
      setOutboundReviewAcknowledged: (v) =>
        set({ outboundReviewAcknowledged: v }),
      outboundStakeholders: [],
      setOutboundStakeholders: (stakeholders) =>
        set((state) =>
          areOutboundStakeholdersEqual(state.outboundStakeholders, stakeholders)
            ? state
            : { outboundStakeholders: stakeholders }
        ),
      outboundRecentlyAddedStakeholderIds: [],
      addOutboundStakeholder: (stakeholder) =>
        set((state) => ({
          // Append so the run's own top-ranked discovery order is undisturbed
          // — outboundRecentlyAddedStakeholderIds is what the table actually
          // uses to pin this row to the top of the DISPLAYED list.
          outboundStakeholders: [...state.outboundStakeholders, stakeholder],
          outboundRecentlyAddedStakeholderIds: [
            ...state.outboundRecentlyAddedStakeholderIds,
            stakeholder.id
          ]
        })),
      removeOutboundStakeholder: (id) =>
        set((state) => ({
          outboundStakeholders: state.outboundStakeholders.filter(
            (s) => s.id !== id
          ),
          outboundRecentlyAddedStakeholderIds:
            state.outboundRecentlyAddedStakeholderIds.filter((i) => i !== id),
          selectedStakeholderIds: state.selectedStakeholderIds.filter(
            (i) => i !== id
          )
        })),
      updateOutboundStakeholder: (id, updates) =>
        set((state) => ({
          outboundStakeholders: state.outboundStakeholders.map((s) =>
            s.id === id ? { ...s, ...updates } : s
          )
        })),
      outboundProgress: { ...INITIAL_OUTBOUND_PROGRESS },
      setOutboundPhase: (phase) =>
        set((state) =>
          state.outboundProgress.phase === phase
            ? state
            : {
                outboundProgress: { ...state.outboundProgress, phase }
              }
        ),
      addOutboundStep: (step) =>
        set((state) => ({
          outboundProgress: {
            ...state.outboundProgress,
            steps: [...state.outboundProgress.steps, step]
          }
        })),
      updateOutboundStep: (stepId, updates) =>
        set((state) => ({
          outboundProgress: {
            ...state.outboundProgress,
            steps: state.outboundProgress.steps.map((s) =>
              s.id === stepId ? { ...s, ...updates } : s
            )
          }
        })),
      setOutboundSteps: (steps) =>
        set((state) =>
          areOutboundStepsEqual(state.outboundProgress.steps, steps)
            ? state
            : {
                outboundProgress: { ...state.outboundProgress, steps }
              }
        ),
      resetOutboundAgent: () =>
        set((state) => ({
          outboundAllMatchedAccounts: [],
          outboundMatchedAccounts: [],
          outboundSelectedMatchedAccountIds: [],
          outboundCommittedAccountIds: [],
          outboundActiveAccountId: null,
          outboundReviewAcknowledged: false,
          outboundStakeholders: [],
          outboundRecentlyAddedStakeholderIds: [],
          outboundRelevantRoles: [],
          outboundLlmTitleExpansions: {},
          outboundProgress: { ...INITIAL_OUTBOUND_PROGRESS },
          selectedStakeholderIds: [] as string[],
          outboundSeenStakeholderIds: [] as string[],
          outboundSeenAccountIds: [] as string[],
          outboundAccountPages: {},
          outboundAutoFetchedRunId: null,
          outboundCustomProspectList: null,
          // Stop the live run + remember it so mount-recovery won't resurrect it.
          outboundDismissedRunId:
            state.outboundCurrentRunId ?? state.outboundDismissedRunId,
          outboundCurrentRunId: null,
          outboundIsRunning: false
        })),

      // Live run lifecycle — shared so a tab-independent parent can own polling.
      outboundCurrentRunId: null,
      // A non-null run id means a run is active again — clear the dismissal so
      // the poller may recover/track it.
      setOutboundCurrentRunId: (runId) =>
        set((state) => ({
          outboundCurrentRunId: runId,
          outboundDismissedRunId:
            runId && runId === state.outboundDismissedRunId
              ? null
              : state.outboundDismissedRunId
        })),
      outboundIsRunning: false,
      setOutboundIsRunning: (v) => set({ outboundIsRunning: v }),
      outboundLatestRunState: "unknown",
      setOutboundLatestRunState: (s) => set({ outboundLatestRunState: s }),
      outboundDismissedRunId: null,
      outboundUploadReviewDismissedRunIds: [] as string[],
      acknowledgeOutboundUploadReview: (runId) =>
        set((state) =>
          !runId || state.outboundUploadReviewDismissedRunIds.includes(runId)
            ? state
            : {
                outboundUploadReviewDismissedRunIds: [
                  ...state.outboundUploadReviewDismissedRunIds,
                  runId
                ].slice(-UPLOAD_REVIEW_ACK_HISTORY)
              }
        ),
      outboundMonitoringPaused: {},
      setOutboundMonitoringPaused: (workflowId, paused) =>
        set((state) => ({
          outboundMonitoringPaused: {
            ...state.outboundMonitoringPaused,
            [workflowId]: paused
          }
        })),

      // PROTOTYPE prospectOS: per-agent session state
      outboundCustomProspectList: null,
      setOutboundCustomProspectList: (payload) =>
        set({ outboundCustomProspectList: payload }),
      outboundSessions: {},
      snapshotOutboundSession: (workflowId) =>
        set((state) => ({
          outboundSessions: {
            ...state.outboundSessions,
            [workflowId]: {
              stakeholders: state.outboundStakeholders,
              progress: state.outboundProgress,
              matchedAccounts: state.outboundMatchedAccounts,
              allMatchedAccounts: state.outboundAllMatchedAccounts,
              customProspectList: state.outboundCustomProspectList,
              selectedStakeholderIds: state.selectedStakeholderIds,
              seenStakeholderIds: state.outboundSeenStakeholderIds,
              currentRunId: state.outboundCurrentRunId,
              isRunning: state.outboundIsRunning
            }
          }
        })),
      loadOutboundSession: (workflowId) => {
        const session = get().outboundSessions[workflowId]
        if (session) {
          set((state) => {
            // Restore the COMMITTED account snapshot (what was selected when the
            // user last opened the People list) if one exists. Marking every
            // restored account as "seen" stops the auto-select-fresh effect from
            // re-adding the whole list on top of the restored selection — the
            // cause of "refresh selects all accounts". With no committed
            // snapshot, fall back to the old fresh auto-select (seen empty).
            const hasCommitted = state.outboundCommittedAccountIds.length > 0
            const restoredAccountIds = (session.allMatchedAccounts || [])
              .map((a) => String(a?.id ?? a?.account_id ?? ""))
              .filter(Boolean)
            return {
              outboundStakeholders: session.stakeholders,
              outboundRecentlyAddedStakeholderIds: [],
              outboundProgress: session.progress,
              outboundMatchedAccounts: session.matchedAccounts,
              outboundAllMatchedAccounts: session.allMatchedAccounts,
              outboundCustomProspectList: session.customProspectList,
              selectedStakeholderIds: session.selectedStakeholderIds,
              outboundSeenStakeholderIds: session.seenStakeholderIds ?? [],
              outboundSeenAccountIds: hasCommitted ? restoredAccountIds : [],
              outboundAccountPages: {},
              // Clear the active rationale account so it re-seeds to the new
              // list's first card (no stale rationale across agent switches).
              outboundActiveAccountId: null,
              // Mark the restored run as already auto-fetched so returning to it
              // doesn't re-issue people_search; Load More still works on demand.
              outboundAutoFetchedRunId: session.currentRunId ?? null,
              outboundCurrentRunId: session.currentRunId ?? null,
              outboundIsRunning: false, // always false on restore; server confirms on mount
              outboundSelectedMatchedAccountIds: hasCommitted
                ? state.outboundCommittedAccountIds
                : [],
              outboundReviewAcknowledged: false,
              // Relevance belongs to the agent that produced it. These are
              // PERSISTED, and the poller only overwrites them when the
              // incoming run reports a non-empty relevant_roles — so without
              // clearing here, switching agents left the previous agent's
              // roles silently filtering this one's people, and a run with no
              // roles of its own never recovered. Empty means "don't filter",
              // which is the right default until this agent's run reports.
              outboundRelevantRoles: [],
              outboundLlmTitleExpansions: {}
            }
          })
        } else {
          set({
            outboundStakeholders: [],
            outboundRecentlyAddedStakeholderIds: [],
            outboundProgress: { ...INITIAL_OUTBOUND_PROGRESS },
            outboundMatchedAccounts: [],
            outboundAllMatchedAccounts: [],
            outboundCustomProspectList: null,
            selectedStakeholderIds: [],
            outboundSeenStakeholderIds: [],
            outboundSeenAccountIds: [],
            outboundAccountPages: {},
            outboundActiveAccountId: null,
            outboundAutoFetchedRunId: null,
            outboundCurrentRunId: null,
            outboundIsRunning: false,
            outboundSelectedMatchedAccountIds: [],
            outboundReviewAcknowledged: false,
            // Same reason as the restore branch above — never carry one
            // agent's role relevance into another.
            outboundRelevantRoles: [],
            outboundLlmTitleExpansions: {}
          })
        }
      },

      // Account plan agent execution
      accountPlanProgress: { ...INITIAL_ACCOUNT_PLAN_PROGRESS },
      setAccountPlanPhase: (phase) =>
        set((state) => ({
          accountPlanProgress: { ...state.accountPlanProgress, phase }
        })),
      addAccountPlanStep: (step) =>
        set((state) => ({
          accountPlanProgress: {
            ...state.accountPlanProgress,
            steps: [...state.accountPlanProgress.steps, step]
          }
        })),
      updateAccountPlanStep: (stepId, updates) =>
        set((state) => ({
          accountPlanProgress: {
            ...state.accountPlanProgress,
            steps: state.accountPlanProgress.steps.map((s) =>
              s.id === stepId ? { ...s, ...updates } : s
            )
          }
        })),
      resetAccountPlan: () =>
        set({
          accountPlanProgress: { ...INITIAL_ACCOUNT_PLAN_PROGRESS }
        }),

      // Stakeholder selection
      selectedStakeholderIds: [] as string[],
      toggleStakeholderSelection: (id) =>
        set((state) => ({
          selectedStakeholderIds: state.selectedStakeholderIds.includes(id)
            ? state.selectedStakeholderIds.filter((i) => i !== id)
            : [...state.selectedStakeholderIds, id]
        })),
      selectAllStakeholders: () =>
        set((state) => ({
          selectedStakeholderIds: state.outboundStakeholders.map((s) => s.id)
        })),
      deselectAllStakeholders: () =>
        set({ selectedStakeholderIds: [] as string[] }),
      setSelectedStakeholderIds: (ids) => set({ selectedStakeholderIds: ids }),
      outboundSeenStakeholderIds: [] as string[],
      markOutboundStakeholdersSeen: (ids) =>
        set((state) => ({
          outboundSeenStakeholderIds: Array.from(
            new Set([...state.outboundSeenStakeholderIds, ...ids])
          )
        })),
      outboundSeenAccountIds: [] as string[],
      markOutboundAccountsSeen: (ids) =>
        set((state) => ({
          outboundSeenAccountIds: Array.from(
            new Set([...state.outboundSeenAccountIds, ...ids])
          )
        })),
      outboundAccountPages: {},
      setOutboundAccountPage: (accountId, entry) =>
        set((state) => ({
          outboundAccountPages: {
            ...state.outboundAccountPages,
            [accountId]: entry
          }
        })),
      outboundAutoFetchedRunId: null,
      setOutboundAutoFetchedRunId: (runId) =>
        set({ outboundAutoFetchedRunId: runId }),

      // Email connection — provider preference only; live status comes from
      // useGetEmailConnectionStatus.
      emailProvider: null,
      setEmailProvider: (provider) => set({ emailProvider: provider }),

      showOutboundSetupWizard: false,
      setShowOutboundSetupWizard: (show) =>
        set({ showOutboundSetupWizard: show }),

      // Dashboard tasks
      tasks: [] as IAgentTask[],
      addTask: (task) => {
        // Optimistic insert with the FE-generated id, then POST. When the
        // server returns a real id, swap it in so future updates target the
        // correct row.
        set((state) => ({ tasks: [task, ...state.tasks] }))
        const tokenInfo = useGlobalStore.getState().tokenInfo
        if (!tokenInfo) return
        createOutboundTask(tokenInfo as ITokenInfo, task)
          .then((res) => {
            const serverTask = res?.data?.data ?? res?.data
            const serverId = serverTask?.id
            if (serverId && serverId !== task.id) {
              set((state) => ({
                tasks: state.tasks.map((t) =>
                  t.id === task.id ? { ...t, id: serverId } : t
                )
              }))
            }
          })
          .catch(() => {
            // eslint-disable-next-line no-console
            console.error("[outbound] failed to persist task", task.id)
          })
      },
      completeTask: (taskId) => {
        const completedAt = new Date().toISOString()
        set((state) => ({
          tasks: state.tasks.map((t) =>
            t.id === taskId
              ? {
                  ...t,
                  status: "completed" as const,
                  completed_at: completedAt
                }
              : t
          )
        }))
        const tokenInfo = useGlobalStore.getState().tokenInfo
        if (!tokenInfo) return
        patchOutboundTask(tokenInfo as ITokenInfo, taskId, {
          status: "completed",
          completed_at: completedAt
        }).catch(() => {
          // eslint-disable-next-line no-console
          console.error("[outbound] failed to persist task complete", taskId)
        })
      },
      snoozeTask: (taskId, until) => {
        set((state) => ({
          tasks: state.tasks.map((t) =>
            t.id === taskId
              ? { ...t, status: "snoozed" as const, snoozed_until: until }
              : t
          )
        }))
        const tokenInfo = useGlobalStore.getState().tokenInfo
        if (!tokenInfo) return
        patchOutboundTask(tokenInfo as ITokenInfo, taskId, {
          status: "snoozed",
          snoozed_until: until
        }).catch(() => {
          // eslint-disable-next-line no-console
          console.error("[outbound] failed to persist task snooze", taskId)
        })
      },
      deleteTask: (taskId) => {
        set((state) => ({
          tasks: state.tasks.filter((t) => t.id !== taskId)
        }))
        const tokenInfo = useGlobalStore.getState().tokenInfo
        if (!tokenInfo) return
        deleteOutboundTask(tokenInfo as ITokenInfo, taskId).catch(() => {
          // eslint-disable-next-line no-console
          console.error("[outbound] failed to delete task", taskId)
        })
      },
      loadTasks: async () => {
        const tokenInfo = useGlobalStore.getState().tokenInfo
        if (!tokenInfo) return
        try {
          const res = await getOutboundTasks(tokenInfo as ITokenInfo)
          const list = (res?.data?.data ?? res?.data ?? []) as IAgentTask[]
          if (Array.isArray(list)) set({ tasks: list })
        } catch {
          // eslint-disable-next-line no-console
          console.error("[outbound] failed to load tasks")
        }
      },

      showDigestPreview: false,
      setShowDigestPreview: (show) => set({ showDigestPreview: show }),

      lastNotification: null,
      setLastNotification: (n) => set({ lastNotification: n })
    }),
    {
      name: "agent-os-store",
      version: 2,
      migrate: (persisted: any) => {
        // v0 → v1: the store previously seeded a synthetic "outbound-default"
        // workflow. That entry has a non-UUID id and will cause a Postgres
        // invalid-UUID 500 if sent to the backend. Strip any such stale rows.
        const UUID_RE =
          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
        if (Array.isArray(persisted?.workflows)) {
          persisted.workflows = persisted.workflows.filter(
            (w: any) =>
              w.workflow_type !== "outbound" || UUID_RE.test(w.id ?? "")
          )
        }
        delete persisted?.outboundStakeholders
        delete persisted?.outboundProgress
        return persisted
      },
      partialize: (state) => ({
        _userId: state._userId,
        _tenantId: state._tenantId,
        tasks: state.tasks,
        workflows: state.workflows,
        workflowRuns: state.workflowRuns,
        selectedStakeholderIds: state.selectedStakeholderIds,
        emailProvider: state.emailProvider,
        // Persist relevance data so the role filter survives refresh — otherwise
        // it resets to [] (unfiltered) until the poller re-hydrates, causing a
        // loose->strict swing. Paired with always-re-hydrate in the poller so
        // these can't go stale across run/workflow switches.
        outboundRelevantRoles: state.outboundRelevantRoles,
        outboundLlmTitleExpansions: state.outboundLlmTitleExpansions,
        // Persist the COMMITTED account selection (snapshotted when the user
        // opens the People list) — NOT the live selection — so a refresh
        // restores exactly what was committed, even if the user later changed
        // the live selection without re-opening People. outboundSeenAccountIds
        // must persist too, else the "auto-select fresh signal accounts" effect
        // treats every account as new on reload and clobbers the restored set.
        outboundCommittedAccountIds: state.outboundCommittedAccountIds,
        outboundSeenAccountIds: state.outboundSeenAccountIds,
        // The uploaded-accounts explainer is a once-per-run message. Without
        // persisting the acknowledgement it reappeared after every reload,
        // since AgentRunner unmounts on each tab switch and its local
        // open-state offers no backstop.
        outboundUploadReviewDismissedRunIds:
          state.outboundUploadReviewDismissedRunIds
      })
      // Cross-user safety handled via setUserContext in the layout
    }
  )
)

export default useAgentOsStore
